import { VERSION_ENDPOINT } from '@worklogix/shared';
import { useEffect, useState } from 'react';

/** Shape of the static `version.json` emitted at build time. */
interface VersionManifest {
  v: string;
}

export interface VersionCheck {
  /** True when the server is advertising a build id different from this build's. */
  updateAvailable: boolean;
  /** The build id currently served by `version.json`, once fetched. */
  latestVersion: string | null;
}

/** How often to poll `version.json` for a newer deploy. */
const POLL_INTERVAL_MS = 60_000;

/**
 * Poll {@link VERSION_ENDPOINT} and compare the served build id against this
 * bundle's `VITE_BUILD_ID`. When they differ (and this build knows its own id),
 * a newer deploy is live and {@link VersionCheck.updateAvailable} flips true.
 *
 * NOTE: if `VITE_BUILD_ID` was not passed at build time the current id is empty
 * and we never flag an update (matches the FLX gotcha — the banner silently
 * never fires when the build id is missing).
 */
export function useVersionCheck(): VersionCheck {
  const currentBuildId = import.meta.env.VITE_BUILD_ID;
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function check(): Promise<void> {
      try {
        const response = await fetch(VERSION_ENDPOINT, { cache: 'no-store' });
        if (!response.ok) {
          return;
        }
        const manifest = (await response.json()) as Partial<VersionManifest>;
        const served = manifest.v;
        if (cancelled || typeof served !== 'string') {
          return;
        }
        setLatestVersion(served);
        if (currentBuildId !== '' && served !== '' && served !== currentBuildId) {
          setUpdateAvailable(true);
        }
      } catch {
        // Network hiccup — leave state unchanged, try again next tick.
      }
    }

    void check();
    const timer = window.setInterval(() => {
      void check();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [currentBuildId]);

  return { updateAvailable, latestVersion };
}
